// English UI strings — the default locale.
//
// Rules shared by every locale file:
// - Proper nouns are never translated: Steam, steamplayti.me, game names,
//   network names (X, Facebook, Reddit), "Steam ID".
// - SEO surfaces (document titles, meta, blog/seo-page views) are NOT keyed
//   here on purpose — they stay English; see src/i18n/index.js.
export default {
  common: {
    loading: 'Loading...',
    updating: 'Updating...',
    never: 'Never',
    hoursMinutes: '{h}h {m}m',
    hoursOnly: '{h}h',
    minutesOnly: '{m}m',
    zeroMinutes: '0m',
    zeroHours: '0h'
  },
  nav: {
    blog: 'Blog',
    brandTitle: 'steamplayti.me — Steam Playtime Tracker',
    blogTitle: 'steamplayti.me blog — Steam news, tips, and guides',
    signIn: 'Sign in',
    signInTitle: 'Sign in with Steam',
    settings: 'Settings'
  },
  settings: {
    signInPerkEmail: 'Choose how often your playtime summary e-mail arrives.',
    signInPerkDashboard: 'Jump straight back to your own dashboard.',
    signInNote: 'Steam handles the sign-in and brings you straight back here. We never see your password.',
    lead: 'Your steamplayti.me account and the e-mails we send you.',
    signInTitle: 'Sign in to see your settings',
    steamIdLabel: 'Steam ID',
    summaryBody: 'A short recap of your Steam playtime, sent to your inbox.',
    emailDesc: 'The address you confirmed when you claimed your profile.',
    frequencyDesc: 'Changes are saved as soon as you pick an option.',
    saving: 'Saving…',
    unclaimedTitle: 'Claim your profile to get the summary',
    metaTitle: 'Settings — steamplayti.me',
    heading: 'Settings',
    signInBody: 'Sign in with Steam to manage your e-mail summary.',
    steamSignInAlt: 'Sign in through Steam',
    loading: 'Loading…',
    unclaimedBody: 'Open your dashboard and use the “Claim this profile” button. Once your e-mail is confirmed, you can choose how often the summary arrives here.',
    emailLabel: 'E-mail',
    recurrenceLabel: 'Summary e-mail',
    frequencyLabel: 'Frequency',
    weeklyHint: 'A recap of your playtime, once a week.',
    disabledHint: 'You will not get the summary e-mail.',
    signedInWithSteam: 'Signed in with Steam',
    weekly: 'Weekly',
    disabled: 'Disabled',
    saved: 'Saved',
    saveFailed: 'Could not save. Try again.',
    loadFailed: 'Could not load your settings right now.',
    visitDashboard: 'Visit your dashboard'
  },
  footer: {
    toolsLabel: 'Steam Tools',
    builtBy: 'Built by',
    gift: 'Gift 🎁',
    donate: 'Donate',
    help: 'Help & Contact',
    about: 'About',
    privacy: 'Privacy'
  },
  landing: {
    heroTagline: 'Steam playtime charts, daily hours, and your full play history.',
    subtitle: "You don't need to wait end of the year to see your retrospective!",
    feature1: 'See daily playtime trends and per-game breakdowns.',
    feature2: 'Spot most-played titles and total hours daily.',
    feature3: 'See your Steam Replay any time, any day -- no waiting for year-end.',
    feature5: 'We are keeping track of your playtime as long as you want.',
    featureFamily: 'Claim your profile and games you play through Steam Family Sharing count too.',
    inputPlaceholder: 'Steam username or SteamID',
    showMyStats: 'Show My Stats',
    privacyNote: 'Set Steam\'s "Game details" privacy toggle to Public so we can pull your library.',
    stayPublicNote: 'Your profile should stay public so that we can keep track of your playtime.',
    expiryNote: 'Profiles that haven\'t been opened in 60 days are removed automatically. Claim yours and it is kept for good — see the button on your dashboard.',
    topGamesTitle: "Yesterday's Top Games",
    topPlayersTitle: "Yesterday's Top Players",
    topAchieversTitle: "Yesterday's Top Achievers",
    uniqueGamesPlayed: 'unique games played',
    trackedAcrossPlayers: 'tracked across all players',
    achievementsUnlocked: 'achievements unlocked',
    exampleProfile: 'Example Profile',
    exampleSince: 'Tracking since September 17, 2025',
    exampleText: 'Explore a synced account with charts, top games, and weekly trends ready to go.',
    viewProfile: 'View the profile'
  },
  overlay: {
    resolvingTitle: 'Resolving Steam Profile',
    syncingTitle: 'Syncing Your Library',
    loadingTitle: 'Loading Playtime Data',
    resolvingMessage: 'Finding your Steam ID...',
    syncingMessage: 'Fetching your games and playtime from Steam. ',
    loadingMessage: 'Preparing charts and stats...',
    syncTip: 'This should not take long. You can leave this page open; it will update automatically when ready.',
    steamIdLabel: 'Steam ID: {id}'
  },
  status: {
    loadingPlaytime: 'Loading playtime data...',
    profileReady: 'Profile ready.',
    stillSyncing: 'Still syncing in the background. You can try again shortly.',
    preparingProfile: 'Preparing profile (may take time if you have many games)...',
    profileFound: 'Profile found. Loading...'
  },
  errors: {
    statsNotPublic: 'Your profile is public but game stats are not. Please make them public and try again.',
    couldNotResolve: 'Could not resolve Steam ID. Please check the username or 17-digit ID.',
    failedToLoadProfile: 'Failed to load this profile.',
    invalidSelection: 'Invalid profile selection.',
    failedToLoadPlaytime: 'Failed to load playtime data for this profile.',
    previewUnavailable: "We couldn't load your library preview right now."
  },
  summary: {
    profile: 'Profile',
    exitProfile: 'Exit profile',
    steamUser: 'Steam user',
    dateRange: 'Date Range',
    selectRange: 'Select range',
    selectDateRange: 'Select date range',
    playtime: 'Playtime',
    avgPerDay: 'Avg: {avg} / day',
    mostPlayed: 'Most Played',
    allTime: 'All-Time Total',
    acrossGames: 'across {count} game | across {count} games',
    ranges: {
      lastWeek: 'Last Week',
      lastTwoWeeks: 'Last Two Weeks',
      last30: 'Last 30 Days',
      last90: 'Last 90 Days',
      last365: 'Last 365 Days',
      thisWeek: 'This Week',
      thisMonth: 'This Month'
    }
  },
  replay: {
    lastUnlocked: 'Last Unlocked',
    unlocked: 'unlocked',
    longestStreak: 'Longest Streak',
    day: 'day | days',
    inThisStreak: '{playtime} in this streak',
    playtimeByMonth: 'Playtime By Month',
    mostPlayedTypes: 'Most Played Types',
    pieCenterTop: 'Top',
    pieCenterTypes: 'types',
    playCalendar: 'Play Calendar',
    less: 'Less',
    more: 'More'
  },
  charts: {
    updatingCharts: 'Updating charts...',
    noDataYet: 'No data yet',
    alreadyCollected: 'We already collected {hours} hours across {games} games.',
    playAndComeBack: 'Play some games and come back tomorrow to see your charts.',
    loadingPreview: 'Loading your library preview...',
    mostPlayedOnSteam: 'Your most played games on Steam',
    showingOf: '(showing {shown} of {total})',
    achievementsUnlockedCount: '{count} achievements unlocked',
    librarySnapshot: 'Library snapshot from today.',
    couldntLoadGames: "We couldn't load your games yet. Try again soon.",
    noDataPeriod: "We don't have any data for this time period.",
    noPlaytimeInRange: 'No playtime in this range',
    tryBroaderFilter: 'Try a broader date filter to explore your recent activity.',
    includeShortPlaytimes: 'Include games with short playtimes',
    totalPlaytimeTrend: 'Total Playtime Trend',
    showTrend: 'Show trend',
    showBars: 'Show bars',
    playtimeAxis: 'Playtime',
    dateAxis: 'Date'
  },
  games: {
    yourGames: 'Your Games',
    showingRange: 'Showing {start}-{end} of {total} games',
    sortBy: 'Sort by:',
    playtime: 'Playtime',
    gameName: 'Game Name',
    lastPlayed: 'Last Played',
    daysPlayed: 'Days Played',
    lastActivity: 'Last Activity',
    show: 'Show:',
    unlockedCount: '{count} unlocked',
    firstPage: 'First page',
    prevPage: 'Previous page',
    nextPage: 'Next page',
    lastPage: 'Last page',
    pageOf: 'Page {page} of {total}'
  },
  achievementHistory: {
    title: 'Achievement History',
    rangeCount: 'No achievements unlocked in this period | {count} achievement unlocked in this period | {count} achievements unlocked in this period',
    empty: 'No achievements unlocked in this period.',
    loading: 'Loading achievements…',
    error: "Couldn't load achievements. Try again later.",
    newest: 'Newest first',
    oldest: 'Oldest first',
    lockedTitle: 'Every achievement, day by day',
    lockedBody: 'The full achievement history is shown on claimed profiles. Claim this profile to unlock it.'
  },
  share: {
    cardLabel: 'Share',
    onX: 'Share on X',
    onFacebook: 'Share on Facebook',
    onReddit: 'Post on Reddit',
    native: 'Share…',
    copyLink: 'Copy link',
    linkCopied: 'Link copied'
  },
  claim: {
    button: 'Claim this profile',
    close: 'Close',
    forcedIntro: "This is a new profile — confirm your email first so we can let you know once your data is ready.",
    step1: 'Step 1 of 2',
    step2: 'Step 2 of 2',
    heading: 'Claim your profile',
    intro: 'Unclaimed profiles are deleted after 60 days without a visit. A claimed one never expires — your playtime history is kept for good, however long you stay away.',
    stepSignIn: 'Sign in through Steam — this proves the profile is yours.',
    stepConfirm: 'Confirm your email. The claim is only complete after that.',
    steamSignInAlt: 'Sign in through Steam',
    signInNote: 'Steam handles the sign-in and brings you straight back here. We never see your password, and we only ever claim the profile that belongs to your account.',
    notPublic: 'Your Steam profile and its Game details both need to be Public before it can be claimed — that is how we read your library. Turn them on in Steam privacy settings, then reload this page.',
    privateHeading: 'Make your library public',
    playtimeHeading: 'Make your playtime visible',
    playtimePrivate: 'We can see your games, but Steam shows 0 hours on every one of them — so there would be nothing to track. In Steam, open Edit Profile → Privacy Settings, untick "Always keep my total playtime private" under Game details, then reload this page.',
    confirmHeading: 'Confirm your email',
    signedInAs: "You're signed in as {id}. Add an email and follow the link we send. Nothing is saved until you do — close this and you start over.",
    // The `@` MUST stay escaped as a `{'@'}` literal. vue-i18n reads a bare @
    // as the start of a linked message (`@:key`), and `@example` is not a valid
    // one — the production build of the compiler *throws* SyntaxError there
    // (dev only warns), which killed the render of this whole modal step and
    // made every ClaimCard silently vanish after the Steam round-trip.
    emailPlaceholder: "you{'@'}example.com",
    emailAria: 'Email address',
    sending: 'Sending...',
    sendLink: 'Send confirmation link',
    providersNote: 'Gmail, Outlook, Hotmail, Yahoo, iCloud, Yandex and Proton addresses.',
    wrongProfile: 'Wrong profile',
    mismatchHeading: "That's not this profile",
    mismatchBody: 'Steam signed you in as {signedId}, but this page belongs to {pageId}. You can only claim the profile your own Steam account owns.',
    goToMyProfile: 'Go to my profile',
    almostThere: 'Almost there',
    checkInbox: 'Check your inbox',
    sentBody: 'We sent a link to {email}. Follow it and your profile is claimed. The link expires in 48 hours, and nothing is stored until you use it.',
    sendAgain: 'Send it again',
    useDifferent: 'Use a different address',
    mailError: 'Could not send the mail. Try again.'
  },
  // Shown once, on landing back from the confirmation link in the mail. The
  // api redirects here with ?confirmed=1|already, or ?confirm_error=... on the
  // landing page. Keep every message free of a bare `@` — see the note on
  // claim.emailPlaceholder.
  claimConfirmed: {
    dismiss: 'Got it',
    successHeading: 'Your profile is claimed',
    successBody: 'That is it — this profile is yours for good now. Your playtime history is kept however long you stay away, and it will never be removed for inactivity.',
    alreadyHeading: 'Already claimed',
    alreadyBody: 'This profile was already claimed, so there was nothing left to do. You are all set.',
    expiredHeading: 'That link no longer works',
    expiredBody: 'Confirmation links expire after 48 hours and can only be used once. Open your profile and start the claim again to get a fresh one.',
    serverHeading: 'Something went wrong',
    serverBody: 'We could not finish the claim just now. Your link may still be valid — try it again in a moment, or start the claim over from your profile.'
  },
  notFound: {
    title: 'Page Not Found',
    text: "We couldn't find that page. Head back to the dashboard to keep tracking playtimes.",
    goHome: 'Go to Homepage'
  }
}
